/**
 * The hand-written knowledge-base description's rules, the same as the
 * `knowledge-base` edge function's (supabase/functions/knowledge-base/
 * validation.ts — change both together), so a description it would refuse
 * never leaves the editor and the reason shows under it, in words. And what
 * a written one is made from: the start of each ready file (description.ts).
 */

import type { KnowledgeBaseFile } from "@/services/knowledge-base.service";

/** It is resent to the voice model on every turn of every call. */
export const DESCRIPTION_MAX_CHARS = 2000;

/** How much of each file's text the description is written from. */
export const TEXT_HEAD_CHARS = 5000;

/** Whether a ready file holds more than the description read: its text, or, for a file from
 * before step 2 (no `text_chars`), its size. */
export const hasLongFile = (files: readonly Pick<KnowledgeBaseFile, "status" | "text_chars" | "file_size">[]) =>
  files.some((file) => file.status === "ready" && (file.text_chars ?? file.file_size) > TEXT_HEAD_CHARS);

/**
 * Characters a reader cannot see that the voice model still reads: controls
 * (tab and newline stay), the soft hyphen, zero-width spaces, word joiners,
 * bidi marks, the BOM and the Unicode tag block. ZWNJ and ZWJ stay: Devanagari
 * spelling and emoji need them.
 */
const INVISIBLE =
  // eslint-disable-next-line no-control-regex -- removing control characters is the point
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u00AD\u061C\u180E\u200B\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]|[\u{E0000}-\u{E007F}]/gu;

/** The description as the function stores it. Empty means "no description". */
export function cleanDescription(text: string): string {
  return text.normalize("NFC").replace(/\r\n?/g, "\n").replace(INVISIBLE, "").trim();
}

const count = (n: number) => n.toLocaleString("en-US");

/** "1,234 / 2,000 characters", counted as the function counts them. */
export function descriptionCount(draft: string): string {
  return `${count(cleanDescription(draft).length)} / ${count(DESCRIPTION_MAX_CHARS)} characters`;
}

/** Why the draft cannot be saved, or `null` when it can. */
export function descriptionError(draft: string): string | null {
  const length = cleanDescription(draft).length;
  if (length <= DESCRIPTION_MAX_CHARS) return null;
  return `Keep the description to ${count(DESCRIPTION_MAX_CHARS)} characters or fewer (it has ${count(length)}).`;
}
